/**
 * SF-4652: the side chat with Eva on the review page. The person asks for a change; sf-api passes
 * it to Eva (POST .../messages) and the page re-reads the build until Eva's new version is there.
 * The chat so far comes back from Eva (GET .../messages) when the page opens, so a reload keeps it.
 * SF-4746: on someone else's build it is read only, the chat without the box to send.
 * SF-4770: Eva's lines (her replies and each version's change set message) show their formatting;
 * the person's own lines stay plain text.
 */
import { useState } from 'react';
import { Alert, Button, Card, Input, List, Space, Spin, Typography } from 'antd';
import { EvaMarkdown } from './EvaMarkdown';

export interface ChatLine {
  from: 'you' | 'eva';
  text: string;
}

export interface ReviewChatProps {
  /** Someone else's build: the chat is shown, the box to send is not. */
  readOnly?: boolean;
  /** Who the person's lines are from: "You", or on someone else's build their name. */
  youLabel?: string;
  lines: ChatLine[];
  /** Eva is working on a change (the build is drafting). */
  working: boolean;
  /** Why the person cannot ask for a change now, if they cannot. */
  closedReason: string | null;
  error: string | null;
  sending: boolean;
  /** Resolves true when sf-api took the message (the box is then cleared). */
  onSend: (text: string) => Promise<boolean>;
  /**
   * SF-4847: the box's text, when the page needs it ("Make version N" sends it with the
   * removals). Without these the box keeps its own text.
   */
  text?: string;
  onTextChange?: (text: string) => void;
}

export const MAX_MESSAGE_LENGTH = 4000; // sf-api demoBuildsController MAX_MESSAGE_LENGTH

export const ReviewChat: React.FC<ReviewChatProps> = ({
  readOnly = false,
  youLabel = 'You',
  lines,
  working,
  closedReason,
  error,
  sending,
  onSend,
  text: shownText,
  onTextChange,
}) => {
  const [ownText, setOwnText] = useState('');
  const text = shownText ?? ownText;
  const setText = onTextChange ?? setOwnText;
  const blocked = working || sending || !!closedReason;
  const send = async () => {
    const value = text.trim();
    if (!value || blocked) return;
    if (await onSend(value)) setText('');
  };
  return (
    <Card
      title={readOnly ? 'Chat with Eva' : 'Ask Eva for a change'}
      size="small"
      data-testid="review-chat"
    >
      <List
        size="small"
        dataSource={lines}
        locale={{
          emptyText: readOnly
            ? 'No messages.'
            : 'Tell Eva what to change, for example "add a survey about cleanliness".',
        }}
        renderItem={(line) => (
          <List.Item data-testid="chat-line" data-from={line.from}>
            <Space direction="vertical" size={0} style={{ width: '100%', minWidth: 0 }}>
              <Typography.Text strong>{line.from === 'eva' ? 'Eva' : youLabel}</Typography.Text>
              {line.from === 'eva' ? (
                <EvaMarkdown text={line.text} />
              ) : (
                <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{line.text}</Typography.Text>
              )}
            </Space>
          </List.Item>
        )}
      />
      {working && (
        <Space style={{ margin: '8px 0' }} data-testid="eva-working">
          <Spin size="small" />
          <Typography.Text type="secondary">Eva is working on the change set…</Typography.Text>
        </Space>
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ margin: '8px 0' }}
          data-testid="chat-error"
        />
      )}
      {!readOnly && closedReason && !working && (
        <Typography.Paragraph type="secondary" style={{ margin: '8px 0' }}>
          {closedReason}
        </Typography.Paragraph>
      )}
      {!readOnly && (
        <Space.Compact style={{ width: '100%', marginTop: 8 }}>
          <Input.TextArea
            aria-label="Message to Eva"
            value={text}
            maxLength={MAX_MESSAGE_LENGTH}
            autoSize={{ minRows: 2, maxRows: 6 }}
            disabled={!!closedReason}
            onChange={(e) => setText(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <Button
            type="primary"
            onClick={() => void send()}
            disabled={blocked || !text.trim()}
            loading={sending}
          >
            Send
          </Button>
        </Space.Compact>
      )}
    </Card>
  );
};
